import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { paymentConfirmationStore } from '@/stores/PaymentConfirmationStore';

const dialogEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Dialog = styled.dialog`
  inline-size: 88px;
  block-size: 88px;
  max-width: none;
  margin: auto;
  padding: 0;
  overflow: visible;
  background: transparent;
  border: 0;
  outline: 0;

  &::backdrop {
    background: rgba(17, 17, 20, 0.24);
    -webkit-backdrop-filter: blur(14px) saturate(120%);
    backdrop-filter: blur(14px) saturate(120%);
  }
`;

const SpinnerSurface = styled.div`
  display: grid;
  width: 88px;
  height: 88px;
  place-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 26px;
  box-shadow:
    0 18px 50px rgba(0, 0, 0, 0.2),
    0 2px 10px rgba(0, 0, 0, 0.07);
  -webkit-backdrop-filter: blur(28px) saturate(145%);
  backdrop-filter: blur(28px) saturate(145%);
  animation: ${dialogEnter} 280ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const Spinner = styled.div`
  width: 38px;
  height: 38px;
  border: 3px solid rgba(0, 122, 255, 0.16);
  border-top-color: #007aff;
  border-radius: 50%;
  animation: ${spin} 820ms linear infinite;
`;

const AppearanceStyles = styled.div`
  @media (prefers-reduced-motion: reduce) {
    ${SpinnerSurface},
    ${Spinner} {
      animation: none;
    }

    ${Spinner} {
      border-color: rgba(0, 122, 255, 0.32);
      border-top-color: #007aff;
    }
  }

  @media (prefers-color-scheme: dark) {
    ${SpinnerSurface} {
      background: rgba(35, 35, 38, 0.92);
      border-color: rgba(255, 255, 255, 0.12);
      box-shadow:
        0 18px 50px rgba(0, 0, 0, 0.48),
        0 2px 10px rgba(0, 0, 0, 0.2);
    }
  }
`;

export const PaymentProcessingOverlay = observer(() => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const { active } = paymentConfirmationStore;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (active && !dialog.open) {
      dialog.showModal();
    }
    else if (!active && dialog.open) {
      dialog.close();
    }
  }, [active]);

  useEffect(() => {
    const finishNavigationAttempt = () => {
      paymentConfirmationStore.finishNavigationAttempt();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        finishNavigationAttempt();
      }
    };

    window.addEventListener('pageshow', finishNavigationAttempt);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', finishNavigationAttempt);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AppearanceStyles>
      <Dialog
        ref={dialogRef}
        aria-busy="true"
        aria-label="Processing payment"
        onCancel={(event) => event.preventDefault()}
      >
        <SpinnerSurface aria-hidden="true">
          <Spinner />
        </SpinnerSurface>
      </Dialog>
    </AppearanceStyles>
  );
});
